<script setup lang="ts">
import { useToast } from '@/shared/composables/UseToast'
import {
  useAdminListReferralSources,
  useAdminCreateReferralSource,
  useAdminUpdateReferralSource,
  getAdminListReferralSourcesQueryKey,
} from '@swipe4work/api-orval'
import type {
  AdminReferralSourceCrudReferralSourceDto,
} from '@swipe4work/models-orval'
import { useQueryClient } from '@tanstack/vue-query'
import { FilterMatchMode } from '@primevue/core'

definePage({
  path: '/data/referral-sources',
  name: '/data/ReferralSources',
  meta: {
    title: 'Verwijzingsbronnen',
  },
})

const toast = useToast()
const queryClient = useQueryClient()

const filters = ref({
  global: {
    value: null as string | null,
    matchMode: FilterMatchMode.CONTAINS,
  },
})

const { data: listData, isPending, isError } = useAdminListReferralSources()

const referralSources = computed(() => listData.value?.referralSources ?? [])

// Create
const showCreateForm = ref(false)
const newName = ref('')
const newVisibleFrom = ref<Date | null>(null)
const newVisibleUntil = ref<Date | null>(null)

const createMutation = useAdminCreateReferralSource({
  mutation: {
    onSuccess: () => {
      toast.success('Aangemaakt', 'De verwijzingsbron is aangemaakt.')
      queryClient.invalidateQueries({ queryKey: getAdminListReferralSourcesQueryKey() })
      showCreateForm.value = false
      newName.value = ''
      newVisibleFrom.value = null
      newVisibleUntil.value = null
    },
    onError: () => {
      toast.error('Fout', 'Er is iets misgegaan bij het aanmaken.')
    },
  },
})

function submitCreate() {
  if (!newName.value.trim()) {
    return
  }

  createMutation.mutate({
    data: {
      name: newName.value.trim(),
      visibleBetween: (newVisibleFrom.value && newVisibleUntil.value)
        ? {
          start: newVisibleFrom.value,
          end: newVisibleUntil.value,
        }
        : undefined,
    },
  })
}

// Edit
const editingRow = ref<AdminReferralSourceCrudReferralSourceDto | null>(null)
const editName = ref('')
const editVisibleFrom = ref<Date | null>(null)
const editVisibleUntil = ref<Date | null>(null)

function startEdit(source: AdminReferralSourceCrudReferralSourceDto) {
  editingRow.value = source
  editName.value = source.name
  editVisibleFrom.value = source.visibleBetween?.start ?? null
  editVisibleUntil.value = source.visibleBetween?.end ?? null
}

function cancelEdit() {
  editingRow.value = null
}

const updateMutation = useAdminUpdateReferralSource({
  mutation: {
    onSuccess: () => {
      toast.success('Bijgewerkt', 'De verwijzingsbron is bijgewerkt.')
      queryClient.invalidateQueries({ queryKey: getAdminListReferralSourcesQueryKey() })
      editingRow.value = null
    },
    onError: () => {
      toast.error('Fout', 'Er is iets misgegaan bij het bijwerken.')
    },
  },
})

function submitEdit() {
  if (!editingRow.value || !editName.value.trim()) {
    return
  }

  updateMutation.mutate({
    data: {
      id: editingRow.value.id,
      name: editName.value.trim(),
      visibleBetween: (editVisibleFrom.value && editVisibleUntil.value)
        ? {
          start: editVisibleFrom.value,
          end: editVisibleUntil.value,
        }
        : undefined,
    },
  })
}

function formatDate(date: Date | null | undefined): string {
  if (!date) {
    return '-'
  }

  return new Date(date).toLocaleDateString('nl-NL')
}
</script>

<template>
  <div class="space-y-5">
    <Card>
      <template #content>
        <div class="flex flex-col gap-4">
          <div class="flex items-center justify-between">
            <h3 class="text-lg font-semibold">
              Verwijzingsbronnen
            </h3>
            <Button
              label="Nieuwe bron"
              icon="fa-regular fa-plus"
              @click="showCreateForm = !showCreateForm"
            />
          </div>

          <!-- Create form -->
          <div v-if="showCreateForm" class="border border-surface-200 rounded bg-surface-50 p-4">
            <h4 class="mb-3 font-semibold">
              Nieuwe verwijzingsbron toevoegen
            </h4>
            <div class="flex flex-col gap-3">
              <div class="flex flex-col gap-1">
                <label for="new-name" class="text-sm font-medium">Naam *</label>
                <InputText
                  id="new-name"
                  v-model="newName"
                  placeholder="Bijv. Instagram"
                  class="w-full"
                />
              </div>
              <div class="grid grid-cols-2 gap-3">
                <div class="flex flex-col gap-1">
                  <label for="new-visible-from" class="text-sm font-medium">Zichtbaar vanaf</label>
                  <DatePicker
                    id="new-visible-from"
                    v-model="newVisibleFrom"
                    date-format="dd-mm-yy"
                    placeholder="Optioneel"
                    class="w-full"
                    show-clear
                  />
                </div>
                <div class="flex flex-col gap-1">
                  <label for="new-visible-until" class="text-sm font-medium">Zichtbaar tot</label>
                  <DatePicker
                    id="new-visible-until"
                    v-model="newVisibleUntil"
                    date-format="dd-mm-yy"
                    placeholder="Optioneel"
                    class="w-full"
                    show-clear
                  />
                </div>
              </div>
              <div class="flex justify-end gap-2">
                <Button
                  label="Annuleren"
                  severity="secondary"
                  @click="showCreateForm = false"
                />
                <Button
                  label="Aanmaken"
                  :disabled="!newName.trim() || createMutation.isPending.value"
                  :loading="createMutation.isPending.value"
                  @click="submitCreate"
                />
              </div>
            </div>
          </div>

          <!-- Search -->
          <IconField>
            <InputIcon class="fa-regular fa-search" />
            <InputText v-model="filters.global.value" placeholder="Zoeken..." class="w-full" />
          </IconField>

          <!-- Table -->
          <div v-if="isPending" class="flex flex-col gap-2">
            <Skeleton v-for="n in 5" :key="n" height="3rem" />
          </div>
          <Message v-else-if="isError" severity="error">
            Er is iets misgegaan bij het laden van de verwijzingsbronnen.
          </Message>
          <DataTable
            v-else
            v-model:filters="filters"
            :value="referralSources"
            :global-filter-fields="['name']"
            sort-field="name"
            :sort-order="1"
            class="w-full"
          >
            <Column field="name" header="Naam" sortable>
              <template #body="{ data: source }">
                <div v-if="editingRow?.id === source.id">
                  <InputText v-model="editName" class="w-full" />
                </div>
                <span v-else>{{ source.name }}</span>
              </template>
            </Column>
            <Column header="Zichtbaar vanaf" sortable sort-field="visibleBetween.start">
              <template #body="{ data: source }">
                <div v-if="editingRow?.id === source.id">
                  <DatePicker
                    v-model="editVisibleFrom"
                    date-format="dd-mm-yy"
                    placeholder="Optioneel"
                    class="w-full"
                    show-clear
                  />
                </div>
                <span v-else>{{ formatDate(source.visibleBetween?.start) }}</span>
              </template>
            </Column>
            <Column header="Zichtbaar tot" sortable sort-field="visibleBetween.end">
              <template #body="{ data: source }">
                <div v-if="editingRow?.id === source.id">
                  <DatePicker
                    v-model="editVisibleUntil"
                    date-format="dd-mm-yy"
                    placeholder="Optioneel"
                    class="w-full"
                    show-clear
                  />
                </div>
                <span v-else>{{ formatDate(source.visibleBetween?.end) }}</span>
              </template>
            </Column>
            <Column header="Acties" style="width: 160px">
              <template #body="{ data: source }">
                <div v-if="editingRow?.id === source.id" class="flex gap-2">
                  <Button
                    icon="fa-regular fa-check"
                    severity="success"
                    size="small"
                    v-tooltip.top="'Opslaan'"
                    :loading="updateMutation.isPending.value"
                    @click="submitEdit"
                  />
                  <Button
                    icon="fa-regular fa-xmark"
                    severity="secondary"
                    size="small"
                    v-tooltip.top="'Annuleren'"
                    @click="cancelEdit"
                  />
                </div>
                <div v-else class="flex gap-2">
                  <Button
                    icon="fa-regular fa-pen"
                    severity="secondary"
                    size="small"
                    v-tooltip.top="'Bewerken'"
                    @click="startEdit(source)"
                  />
                </div>
              </template>
            </Column>
          </DataTable>
        </div>
      </template>
    </Card>
  </div>
</template>
